"use client";

import React, { useMemo } from "react";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";
import {
  CopyButton,
  DebugCodePath,
  DebugFilePath,
  DebugSection,
  DebugSourceDisplay,
} from "../components/debug-primitives";
import type { ActiveTab, RouteManifest } from "../debug-panel.types";
import { useActiveTabs } from "../hooks/use-active-tabs";
import { useFileLines } from "../utils/file-lines";
import { sourcesEqual } from "../utils/tab-source";
import { RenderCounterPanel } from "./render-counter-panel";
import { RenderedComponentsSection } from "./rendered-components-section";

function matchPathnameToManifest(
  pathname: string,
  manifest: RouteManifest | null,
) {
  if (!manifest) return null;
  if (manifest.pages[pathname]) return manifest.pages[pathname];
  for (const [pattern, entry] of Object.entries(manifest.pages)) {
    if (!pattern.includes("[")) continue;
    const regex = new RegExp(
      "^" + pattern.replace(/\[([^\]]+)\]/g, "[^/]+") + "$",
    );
    if (regex.test(pathname)) return entry;
  }
  return null;
}

export function FilesTab({
  manifest,
  pathname,
  overlays,
}: {
  manifest: RouteManifest | null;
  pathname: string;
  overlays: { id: string; type: string; sourcePath: string; label?: string }[];
}) {
  const t = useTranslations("debug.files");
  const match = useMemo(
    () => matchPathnameToManifest(pathname, manifest),
    [pathname, manifest],
  );
  const activeTabs = useActiveTabs();
  const allPaths = useMemo(() => {
    const list: string[] = [];
    if (match?.file) list.push(match.file);
    if (match?.layouts) list.push(...match.layouts);
    for (const tab of activeTabs) {
      if (tab.source?.kind === "file") list.push(tab.source.path);
      if (tab.contentSource?.kind === "file") list.push(tab.contentSource.path);
      if (tab.siblingTabs) {
        for (const sib of tab.siblingTabs) {
          if (sib.path) list.push(sib.path);
        }
      }
    }
    for (const overlay of overlays) {
      if (overlay.sourcePath) list.push(overlay.sourcePath);
    }
    return list;
  }, [match, activeTabs, overlays]);
  useFileLines(allPaths);

  const [fileQuery, setFileQuery] = React.useState("");
  const q = fileQuery.trim().toLowerCase();
  const matchesPath = (path: string) => !q || path.toLowerCase().includes(q);

  return (
    <div className="space-y-1">
      {/* Search input */}
      <div className="px-1 pb-1">
        <input
          type="text"
          value={fileQuery}
          onChange={(e) => setFileQuery(e.target.value)}
          placeholder={t("filterPlaceholder")}
          className="w-full h-7 px-2.5 rounded border border-border bg-muted/40 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>
      {(!q || (match && matchesPath(match.file))) && (
        <DebugSection
          title={t("currentPage")}
          accent="border-purple-500"
          forceOpen={!!q}
        >
          <div className="flex items-center gap-1 flex-wrap text-sm mb-2">
            {pathname
              .split("/")
              .filter(Boolean)
              .map((seg, i, arr) => (
                <React.Fragment key={i}>
                  <span
                    className={
                      i === arr.length - 1
                        ? "font-semibold text-foreground"
                        : "text-muted-foreground"
                    }
                  >
                    {seg}
                  </span>
                  {i < arr.length - 1 && (
                    <span className="text-muted-foreground/40">{"/"}</span>
                  )}
                </React.Fragment>
              ))}
          </div>
          {match ? (
            <DebugFilePath path={match.file} />
          ) : (
            <div className="text-sm text-amber-600 dark:text-amber-400">
              {manifest ? t("noManifestMatch") : t("manifestNotLoaded")}
            </div>
          )}
        </DebugSection>
      )}
      {match?.layouts &&
        match.layouts.length > 0 &&
        (() => {
          const visibleLayouts = match.layouts.filter(matchesPath);
          if (q && visibleLayouts.length === 0) return null;
          return (
            <DebugSection
              title={t("layoutChain")}
              accent="border-purple-500"
              forceOpen={!!q}
            >
              {(q ? visibleLayouts : match.layouts).map((layout) => (
                <DebugFilePath key={layout} path={layout} />
              ))}
            </DebugSection>
          );
        })()}
      {activeTabs.length > 0 &&
        (() => {
          const visibleTabs = activeTabs.filter((tab: ActiveTab) => {
            if (!q) return true;
            const srcPath = tab.source?.kind === "file" ? tab.source.path : "";
            const cntPath =
              tab.contentSource?.kind === "file" ? tab.contentSource.path : "";
            return (
              matchesPath(srcPath) ||
              matchesPath(cntPath) ||
              tab.label.toLowerCase().includes(q)
            );
          });
          if (visibleTabs.length === 0) return null;
          return (
            <DebugSection
              title={
                q
                  ? t("activeTabsFiltered", {
                      visible: visibleTabs.length,
                      total: activeTabs.length,
                    })
                  : t("activeTabs", { count: activeTabs.length })
              }
              accent="border-emerald-500"
            >
              <div className="divide-y divide-border/40">
                {visibleTabs.map((tab, i) => (
                  <div key={i} className="py-3 first:pt-0 last:pb-0 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <span className="text-sm font-semibold">{tab.label}</span>
                    </div>
                    {tab.source && (
                      <div className="flex items-center gap-2 ml-3.5 text-sm group">
                        <span className="text-muted-foreground w-16 shrink-0">
                          {t("source")}
                        </span>
                        <DebugSourceDisplay source={tab.source} />
                        {tab.source.kind === "file" && (
                          <CopyButton text={tab.source.path} />
                        )}
                      </div>
                    )}
                    {tab.contentSource &&
                      !sourcesEqual(tab.source, tab.contentSource) && (
                        <div className="flex items-center gap-2 ml-3.5 text-sm group">
                          <span className="text-muted-foreground w-16 shrink-0">
                            {t("content")}
                          </span>
                          <DebugSourceDisplay source={tab.contentSource} />
                          {tab.contentSource.kind === "file" && (
                            <CopyButton text={tab.contentSource.path} />
                          )}
                        </div>
                      )}
                    {tab.siblingTabs && tab.siblingTabs.length > 0 && (
                      <div className="space-y-0.5 ml-3.5 pt-1">
                        {tab.siblingTabs.map((sib, j) => (
                          <div
                            key={j}
                            className="flex items-center gap-2 group"
                          >
                            <span
                              className={cn(
                                "shrink-0 px-2 py-0.5 rounded-md text-sm",
                                sib.isActive
                                  ? "bg-primary/10 text-primary font-medium"
                                  : "bg-muted text-muted-foreground",
                              )}
                            >
                              {sib.label}
                            </span>
                            {sib.path && (
                              <>
                                <DebugCodePath
                                  path={sib.path}
                                  className="text-muted-foreground flex-1"
                                />
                                <CopyButton text={sib.path} />
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </DebugSection>
          );
        })()}
      <RenderedComponentsSection
        pathname={pathname}
        {...(q ? { filter: q } : {})}
        forceOpen={!!q}
      />
      {(() => {
        const visibleOverlays = q
          ? overlays.filter((o) => matchesPath(o.sourcePath))
          : overlays;
        if (q && visibleOverlays.length === 0) return null;
        return (
          <DebugSection
            title={
              q
                ? t("overlaysFiltered", {
                    visible: visibleOverlays.length,
                    total: overlays.length,
                  })
                : t("overlays", { count: overlays.length })
            }
            accent="border-orange-500"
            defaultOpen={overlays.length > 0}
            forceOpen={!!q}
          >
            {overlays.length > 0 ? (
              visibleOverlays.map((overlay) => (
                <div
                  key={overlay.id}
                  className="flex items-center gap-3 text-sm group"
                >
                  <span className="px-2 py-0.5 rounded-md bg-orange-500/10 text-orange-600 dark:text-orange-400 text-sm capitalize font-medium">
                    {overlay.type}
                  </span>
                  <span className="font-medium">
                    {overlay.label || overlay.sourcePath.split("/").pop()}
                  </span>
                  <span className="text-muted-foreground/50 mx-0.5">{"→"}</span>
                  <DebugCodePath
                    path={overlay.sourcePath}
                    className="text-muted-foreground flex-1"
                  />
                  <CopyButton text={overlay.sourcePath} />
                </div>
              ))
            ) : (
              <div className="text-sm text-muted-foreground">
                {t("noOverlays")}
              </div>
            )}
          </DebugSection>
        );
      })()}
      <DebugSection
        title={t("renderPerformance")}
        accent="border-amber-500"
        defaultOpen={true}
        forceOpen={!!q}
      >
        <RenderCounterPanel />
      </DebugSection>
    </div>
  );
}
